/**
 * ORION conversielaag bovenop Horizon.
 *
 * Alle elementen hier zijn eerlijk ingericht: tijden, voorraad en deadlines komen uit echte data
 * (thema-instellingen, metavelden, de winkelwagen). Geen nep-tellers of nep-schaarste: dat is in de
 * EU een oneerlijke handelspraktijk (ACM/Omnibus) en het kost vertrouwen.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const orionScrollRoot = () => {
  const wrap = document.querySelector('.page-wrapper');
  return wrap && wrap.scrollHeight > wrap.clientHeight + 5 ? wrap : document.scrollingElement || document.documentElement;
};

const orionScrollToElement = (element, { block = 'start', offset = 84 } = {}) => {
  if (!(element instanceof Element)) return;
  const root = orionScrollRoot();
  const rect = element.getBoundingClientRect();
  const rootRect = root === document.scrollingElement ? { top: 0 } : root.getBoundingClientRect();
  const current = root === document.scrollingElement ? window.scrollY : root.scrollTop;
  let top = rect.top - rootRect.top + current - offset;
  if (block === 'center') top = rect.top - rootRect.top + current - window.innerHeight / 2 + rect.height / 2;
  root.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
};

const storage = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* privé-venster of geblokkeerde opslag: niets bewaren */
    }
  },
};

/* ------------------------------------------------------------------ */
/* Levertijd: "Bestel binnen 2u 14m, in huis tussen di 7 en do 9 okt"  */
/* ------------------------------------------------------------------ */

class OrionDeliveryEstimate extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.render();
    this.#timer = window.setInterval(() => this.render(), 30_000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  get #config() {
    const d = this.dataset;
    return {
      min: Number(d.minDays ?? 2),
      max: Number(d.maxDays ?? 5),
      cutoffHour: Number(d.cutoffHour ?? 23),
      cutoffMinute: Number(d.cutoffMinute ?? 59),
      shipDays: (d.shipDays ?? '1,2,3,4,5').split(',').map(Number),
      deliveryDays: (d.deliveryDays ?? '1,2,3,4,5').split(',').map(Number),
      closed: (d.closedDates ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      locale: d.locale || document.documentElement.lang || 'nl',
    };
  }

  /** @param {Date} date @param {number[]} allowed @param {string[]} closed */
  static #isOpen(date, allowed, closed) {
    const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return allowed.includes(date.getDay()) && !closed.includes(iso);
  }

  /** Telt `days` dagen op waarop `allowed` geldt. */
  static #addDays(start, days, allowed, closed) {
    const date = new Date(start);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      if (OrionDeliveryEstimate.#isOpen(date, allowed, closed)) added += 1;
    }
    return date;
  }

  /** Huidige wandkloktijd in Amsterdam (verzending gebeurt daar), los van de tijdzone van de bezoeker. */
  static #amsterdamNow() {
    const now = new Date();
    try {
      return new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }));
    } catch {
      return now;
    }
  }

  render() {
    const c = this.#config;
    const now = OrionDeliveryEstimate.#amsterdamNow();
    const cutoff = new Date(now);
    cutoff.setHours(c.cutoffHour, c.cutoffMinute, 0, 0);

    // Verzenddag: vandaag als het een verzenddag is en we vóór de deadline zitten, anders de eerstvolgende.
    let ship = new Date(now);
    const shipsToday = OrionDeliveryEstimate.#isOpen(now, c.shipDays, c.closed) && now < cutoff;
    if (!shipsToday) {
      do {
        ship.setDate(ship.getDate() + 1);
      } while (!OrionDeliveryEstimate.#isOpen(ship, c.shipDays, c.closed));
    }

    const from = OrionDeliveryEstimate.#addDays(ship, c.min, c.deliveryDays, c.closed);
    const to = OrionDeliveryEstimate.#addDays(ship, c.max, c.deliveryDays, c.closed);
    const fmt = new Intl.DateTimeFormat(c.locale, { weekday: 'short', day: 'numeric', month: 'short' });

    const rangeEl = this.querySelector('[data-orion-range]');
    if (rangeEl) {
      rangeEl.textContent = c.min === c.max ? fmt.format(from) : `${fmt.format(from)} – ${fmt.format(to)}`;
    }

    const countdownEl = this.querySelector('[data-orion-countdown]');
    const countdownWrap = this.querySelector('[data-orion-countdown-wrap]');
    if (countdownEl && countdownWrap) {
      if (shipsToday) {
        const mins = Math.max(0, Math.floor((cutoff.getTime() - now.getTime()) / 60_000));
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        countdownEl.textContent = h > 0 ? `${h}u ${m}m` : `${m}m`;
        countdownWrap.hidden = false;
      } else {
        countdownWrap.hidden = true;
      }
    }
    this.removeAttribute('data-pending');
  }
}

/* ------------------------------------------------------------------ */
/* Actie-afteller: telt af naar een ECHTE einddatum en verdwijnt dan    */
/* ------------------------------------------------------------------ */

class OrionCountdown extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    const end = Date.parse(this.dataset.end ?? '');
    if (Number.isNaN(end)) {
      this.hidden = true;
      return;
    }
    this.end = end;
    this.#decorate(end);
    this.tick();
    this.#schedule();
    this.#io = new IntersectionObserver((entries) => {
      this.#visible = entries.some((e) => e.isIntersecting);
      this.#schedule();
    });
    this.#io.observe(this);
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  #io = /** @type {IntersectionObserver|null} */ (null);
  #visible = true;
  #onVisibility = () => this.#schedule();

  #schedule() {
    window.clearInterval(this.#timer);
    if (!this.#visible || document.hidden) return;
    const left = this.end - Date.now();
    // Meer dan een uur: minuten volstaan (seconden verborgen), minder: per seconde.
    const step = 1000; // per seconde zolang in beeld (pauzeert buiten beeld en bij een verborgen tabblad)
    this.toggleAttribute('data-coarse', false);
    this.tick();
    this.#timer = window.setInterval(() => this.tick(), step);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
    this.#io?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  /** Einddatum leesbaar tonen, laten zien dat de code actief is, en na het activeren terugscrollen. */
  #decorate(end) {
    const date = new Date(end);
    const day = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long' }).format(date);
    const full = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(date);
    this.querySelectorAll('[data-orion-end-date]').forEach((el) => (el.textContent = day));
    const sr = this.querySelector('[data-orion-end-sr]');
    if (sr) sr.textContent = `De actie loopt tot ${full}.`;

    const link = /** @type {HTMLAnchorElement|null} */ (this.querySelector('.orion-countdown__apply'));
    const code = (link?.dataset.code ?? '').toUpperCase();
    const applied = document.cookie
      .split('; ')
      .find((c) => c.startsWith('discount_code='))
      ?.split('=')[1];
    if (link && code && applied && decodeURIComponent(applied).toUpperCase() === code) {
      link.hidden = true;
      const active = this.querySelector('.orion-countdown__active');
      if (active) active.hidden = false;
    }
    link?.addEventListener('click', () => storage.set('orion-countdown-return', '1'));
    if (storage.get('orion-countdown-return') === '1') {
      storage.set('orion-countdown-return', '0');
      // na het laden (Horizon zet de scrollpositie zelf terug), dan pas naar de afteller
      const go = () =>
        window.setTimeout(() => {
          const el = document.querySelector('orion-countdown') ?? this;
          const wrap = document.querySelector('.page-wrapper');
          const scroller = wrap && wrap.scrollHeight > wrap.clientHeight + 5 ? wrap : document.scrollingElement;
          if (!scroller) return;
          const top = el.getBoundingClientRect().top + scroller.scrollTop - window.innerHeight / 2 + el.clientHeight / 2;
          scroller.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
        }, 300);
      if (document.readyState === 'complete') go();
      else window.addEventListener('load', go, { once: true });
    }
  }

  tick() {
    const campaignLeft = this.end - Date.now();
    if (campaignLeft <= 0) {
      window.clearInterval(this.#timer);
      if (this.dataset.endedBehavior === 'hide') {
        this.hidden = true;
      } else {
        this.setAttribute('data-ended', '');
      }
      return;
    }

    // Commercieel: de lanceringsactie loopt t/m einddatum, maar de klok zelf
    // reset elke dag om 00:00 Amsterdam-tijd. Geen willekeurige "12 dagen" meer.
    const now = new Date();
    let amsterdamNow;
    try {
      amsterdamNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }));
    } catch {
      amsterdamNow = now;
    }
    const midnight = new Date(amsterdamNow);
    midnight.setDate(midnight.getDate() + 1);
    midnight.setHours(0, 0, 0, 0);
    const left = Math.min(campaignLeft, midnight.getTime() - amsterdamNow.getTime());
    const s = Math.max(0, Math.floor(left / 1000));
    const parts = {
      d: 0,
      h: Math.floor(s / 3600),
      m: Math.floor((s % 3600) / 60),
      s: s % 60,
    };
    for (const [unit, value] of Object.entries(parts)) {
      const el = this.querySelector(`[data-unit="${unit}"]`);
      if (el) el.textContent = String(value).padStart(2, '0');
    }
    const days = this.querySelector('[data-unit-wrap="d"]');
    if (days) days.hidden = true;
    this.hidden = false;
  }
}

/* ------------------------------------------------------------------ */
/* Bundelkorting: kiest het aantal in het productformulier             */
/* ------------------------------------------------------------------ */

class OrionBundleOffer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    const checked = /** @type {HTMLInputElement|null} */ (this.querySelector('input[type="radio"]:checked'));
    if (checked && Number(checked.value) > 1) this.#apply(Number(checked.value));
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  #onChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (input?.type !== 'radio') return;
    this.#apply(Number(input.value));
  };

  /** @param {number} quantity */
  #apply(quantity) {
    const scope = this.closest('.shopify-section') ?? document;

    // 1. Zichtbare aantal-kiezer (als het blok Aantal in de koopknoppen staat).
    const selector = /** @type {any} */ (scope.querySelector('quantity-selector-component'));
    if (selector && typeof selector.setValue === 'function') {
      selector.setValue(String(quantity));
      return;
    }

    // 2. Geen aantal-kiezer: verborgen veld in het productformulier zetten.
    const form = scope.querySelector('product-form-component form[action*="/cart/add"]');
    if (!form) return;
    let hidden = /** @type {HTMLInputElement|null} */ (form.querySelector('input[name="quantity"]'));
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'quantity';
      form.append(hidden);
    }
    hidden.value = String(quantity);
  }
}

/* ------------------------------------------------------------------ */
/* Upsell in de winkelwagen: toevoegen zonder de pagina te verlaten     */
/* ------------------------------------------------------------------ */

class OrionUpsellAdd extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#add);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#add);
  }

  #add = async () => {
    const select = /** @type {HTMLSelectElement|null} */ (this.querySelector('select'));
    const variantId = select?.value || this.dataset.variantId;
    if (!variantId || !this.button || this.button.disabled) return;

    const button = this.button;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);
    if (!sectionIds.includes('cart-drawer-section')) sectionIds.push('cart-drawer-section');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1, properties: { _orion_bron: 'winkelwagen-upsell' } }],
          sections: [...new Set(sectionIds)].join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Toevoegen mislukt');

      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'orion-upsell', sections: data.sections, didError: false },
      });

      // Horizon hoort de drawer via CartLinesUpdateEvent te morphen. Op mobiele browsers kan die
      // listener soms niet meeliften wanneer de upsell-knop in de drawer zelf zit. Daarom doen we
      // kort daarna een veilige section-fallback: cart-items-component vervangen, drawer open houden.
      window.setTimeout(() => this.#fallbackRenderCartDrawer(data.sections), 350);
    } catch (error) {
      console.error('[orion-upsell]', error);
      deferred.reject(error);
      this.#showError(button);
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  };

  #fallbackRenderCartDrawer(sections) {
    const html = sections?.['cart-drawer-section'];
    if (!html) return;
    const currentDrawer = document.querySelector('#cart-drawer');
    const currentComponent = currentDrawer?.querySelector('cart-items-component');
    if (!currentDrawer || !currentComponent) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextComponent = doc.querySelector('#cart-drawer cart-items-component');
    const nextHeading = doc.querySelector('#cart-drawer-heading');
    const currentHeading = document.querySelector('#cart-drawer-heading');
    if (!nextComponent) return;

    currentComponent.replaceWith(nextComponent);
    if (nextHeading && currentHeading) currentHeading.replaceWith(nextHeading);
    currentDrawer.setAttribute('open', '');
    currentDrawer.querySelector('dialog')?.setAttribute('open', '');
  }

  #showError(button) {
    const oldLabel = button.getAttribute('aria-label') || 'Toevoegen';
    button.setAttribute('aria-label', 'Toevoegen mislukt, probeer opnieuw');
    window.setTimeout(() => button.setAttribute('aria-label', oldLabel), 2500);
  }
}

/* ------------------------------------------------------------------ */
/* Dialoog-trigger (maattabel, info-popups)                            */
/* ------------------------------------------------------------------ */

class OrionDialogTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#open);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#open);
  }

  #open = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-orion-open]')) return;
    const dialog = /** @type {HTMLDialogElement|null} */ (document.getElementById(this.dataset.dialog ?? ''));
    if (!dialog) return;
    // Dialoog naar <body> verplaatsen zodat hij niet in een sticky/overflow-container vastzit.
    if (dialog.parentElement !== document.body) document.body.append(dialog);
    dialog.showModal();
    dialog.addEventListener(
      'click',
      (e) => {
        if (e.target === dialog || (e.target instanceof Element && e.target.closest('[data-orion-close]'))) dialog.close();
      },
      { signal: this.#closeSignal(dialog) }
    );
  };

  #closeSignal(dialog) {
    const controller = new AbortController();
    dialog.addEventListener('close', () => controller.abort(), { once: true });
    return controller.signal;
  }
}

/* ------------------------------------------------------------------ */
/* Nieuwsbrief-popup: na X seconden, scrolldiepte of exit-intent        */
/* ------------------------------------------------------------------ */

class OrionPopup extends HTMLElement {
  #controller = new AbortController();
  #timer = 0;
  #force = false;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    const key = `orion-popup:${this.dataset.key ?? 'default'}`;
    this.storageKey = key;
    this.#bindClose();

    // Na inschrijving (of een fout) laadt Shopify de pagina opnieuw: popup direct weer tonen met de uitkomst.
    if (this.hasAttribute('data-posted') || this.hasAttribute('data-error')) {
      if (this.hasAttribute('data-posted')) storage.set(key, String(Date.now() + 365 * 864e5));
      this.#force = true;
      this.#show();
      return;
    }

    if (window.Shopify?.designMode) {
      if (this.hasAttribute('data-preview')) {
        this.#force = true;
        this.#show();
      }
      return;
    }

    const snoozedUntil = Number(storage.get(key) ?? 0);
    if (snoozedUntil > Date.now()) return;

    const { signal } = this.#controller;
    const delay = Number(this.dataset.delay ?? 0) * 1000;
    const startTimer = () => {
      window.clearTimeout(this.#timer);
      if (delay > 0) this.#timer = window.setTimeout(() => this.#show(), delay);
    };
    const waitForHero = this.dataset.waitHero === 'true' && document.querySelector('.orion-hero--video');
    if (waitForHero) {
      document.addEventListener('orion:hero-ended', startTimer, { once: true, signal });
      // Fallback: voorkom dat de popup nooit komt als autoplay of metadata hapert.
      this.#timer = window.setTimeout(() => this.#show(), Math.max(delay + 12000, 16000));
    } else {
      startTimer();
    }

    if (!waitForHero && this.dataset.exitIntent === 'true') {
      document.documentElement.addEventListener(
        'mouseleave',
        (e) => {
          if (e.clientY <= 0) this.#show();
        },
        { signal }
      );
      // Mobiel heeft geen muis: 55% scrolldiepte als signaal.
      window.addEventListener(
        'scroll',
        () => {
          const depth = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
          if (depth > 0.55 && window.matchMedia('(pointer: coarse)').matches) this.#show();
        },
        { passive: true, signal }
      );
    }

    this.dialog.addEventListener('close', () => {
      const days = Number(this.dataset.snoozeDays ?? 7);
      storage.set(key, String(Date.now() + days * 864e5));
    });
  }

  #bindClose() {
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog || (e.target instanceof Element && e.target.closest('[data-orion-close]'))) {
        this.dialog?.close();
      }
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
    window.clearTimeout(this.#timer);
  }

  #show() {
    // Eén kans per paginaweergave: timer, exit-intent en scroll vallen allemaal weg zodra er één afgaat.
    this.#controller.abort();
    window.clearTimeout(this.#timer);
    if (!this.dialog || this.dialog.open) return;
    if (!this.#force && Number(storage.get(this.storageKey) ?? 0) > Date.now()) return;
    if (document.querySelector('dialog[open]:not(.orion-popup__dialog)')) return; // nooit over een andere dialoog heen
    this.dialog.showModal();
  }
}

/* ------------------------------------------------------------------ */
/* UGC-video's: alleen afspelen wat in beeld is                        */
/* ------------------------------------------------------------------ */

class OrionVideoRail extends HTMLElement {
  connectedCallback() {
    const videos = [...this.querySelectorAll('video')];
    if (!videos.length) return;

    if (!prefersReducedMotion() && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const video = /** @type {HTMLVideoElement} */ (entry.target);
            if (entry.isIntersecting) {
              if (video.preload === 'none') video.preload = 'metadata';
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          }
        },
        { threshold: 0.6 }
      );
      videos.forEach((v) => this.observer?.observe(v));
    }

    this.addEventListener('click', (event) => {
      const btn = event.target instanceof Element ? event.target.closest('[data-orion-sound]') : null;
      if (!btn) return;
      const video = btn.closest('.orion-ugc__item')?.querySelector('video');
      if (!video) return;
      video.muted = !video.muted;
      if (!video.muted) video.play().catch(() => {});
      btn.setAttribute('aria-pressed', String(!video.muted));
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}


/* ------------------------------------------------------------------ */
/* Contactformulier: velden per onderwerp tonen en verplicht maken      */
/* ------------------------------------------------------------------ */

class OrionContactForm extends HTMLElement {
  connectedCallback() {
    this.select = /** @type {HTMLSelectElement|null} */ (this.querySelector('[data-orion-subject-select]'));
    if (!this.select) return;

    const wanted = new URLSearchParams(window.location.search).get('onderwerp');
    if (wanted && [...this.select.options].some((o) => o.value === wanted)) this.select.value = wanted;

    this.select.addEventListener('change', () => this.update());
    // Snelkeuzes op dezelfde pagina: onderwerp zetten zonder herladen
    document.querySelectorAll('[data-orion-subject]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const value = /** @type {HTMLElement} */ (link).dataset.orionSubject;
        if (!value || !this.select) return;
        event.preventDefault();
        this.select.value = value;
        this.update();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', `?onderwerp=${value}#orion-contact-form`);
      });
    });
    this.update();
  }

  update() {
    const subject = this.select?.value ?? '';
    this.querySelectorAll('[data-show-for]').forEach((el) => {
      const show = /** @type {HTMLElement} */ (el).dataset.showFor?.split(' ').includes(subject);
      /** @type {HTMLElement} */ (el).hidden = !show;
      // Verborgen velden niet versturen
      el.querySelectorAll('input, select, textarea').forEach((field) => {
        /** @type {HTMLInputElement} */ (field).disabled = !show;
      });
    });
    this.querySelectorAll('[data-required-for]').forEach((field) => {
      const req = /** @type {HTMLElement} */ (field).dataset.requiredFor?.split(' ').includes(subject);
      /** @type {HTMLInputElement} */ (field).required = Boolean(req);
    });
  }
}


/* ------------------------------------------------------------------ */
/* Koppen met lange woorden (CONTACTGEGEVENS) laten krimpen i.p.v. het */
/* woord af te breken: brede kapitalen + Nederlandse samenstellingen.  */
/* ------------------------------------------------------------------ */

const USU_FIT_SELECTOR = 'h1, h2, .h1, .h2, .shopify-policy__title h1';

function orionFitHeadings() {
  document.querySelectorAll(USU_FIT_SELECTOR).forEach((el) => {
    const heading = /** @type {HTMLElement} */ (el);
    if (heading.closest('.visually-hidden, .orion-visually-hidden') || heading.clientWidth < 40) return; // verborgen
    heading.style.fontSize = '';
    let size = parseFloat(getComputedStyle(heading).fontSize);
    let guard = 0;
    while (heading.scrollWidth > heading.clientWidth + 1 && size > 14 && guard++ < 40) {
      size *= 0.95;
      heading.style.fontSize = `${size}px`;
    }
  });
}

document.documentElement.classList.add('orion-js');
let orionFitFrame = 0;
const orionScheduleFit = () => {
  cancelAnimationFrame(orionFitFrame);
  orionFitFrame = requestAnimationFrame(orionFitHeadings);
};
orionScheduleFit();
document.fonts?.ready.then(orionScheduleFit);
window.addEventListener('load', orionScheduleFit);
let orionLastWidth = window.innerWidth;
window.addEventListener('resize', () => {
  if (window.innerWidth === orionLastWidth) return;
  orionLastWidth = window.innerWidth;
  orionScheduleFit();
});


/* ------------------------------------------------------------------ */
/* "Kies je maat": geen maat vooraf gekozen op de productpagina.       */
/* Horizon houdt intern een variant geselecteerd (nodig voor zijn      */
/* logica); wij tonen de maatknoppen als niet-gekozen en blokkeren de  */
/* koopknoppen tot de klant zelf een maat aantikt.                     */
/* ------------------------------------------------------------------ */

function orionSizeGate() {
  const SIZE_OPTION = 'Maat';
  // Een ?variant=-link (advertentie, Google Shopping, zoekresultaat) is geen maatkeuze van de klant.
  // Alleen wie vanuit de eigen winkelwagen terugkomt, heeft al bewust een maat gekozen.
  const fromCart = (() => {
    try {
      const ref = new URL(document.referrer);
      return ref.origin === window.location.origin && ref.pathname.startsWith('/cart');
    } catch {
      return false;
    }
  })();
  if (fromCart && new URLSearchParams(window.location.search).has('variant')) return;
  if (!document.querySelector(`main variant-picker input[data-option-name="${SIZE_OPTION}"]`)) return;

  let chosen = false;
  const LABEL = 'Kies je maat';

  const apply = () => {
    if (chosen) return;
    document.documentElement.classList.add('orion-needs-size');
    document.querySelectorAll(`main variant-picker input[data-option-name="${SIZE_OPTION}"]`).forEach((input) => {
      input.closest('fieldset')?.classList.add('orion-size-pending');
    });
    document.querySelectorAll('button[name="add"]').forEach((btn) => {
      const text = [...btn.childNodes].find((n) => n.nodeType === 3 && n.textContent.trim());
      if (text && text.textContent.trim() !== LABEL) {
        btn.dataset.orionText = text.textContent;
        text.textContent = LABEL;
      }
      if (!btn.hasAttribute('data-orion-locked')) {
        btn.setAttribute('data-orion-locked', btn.disabled ? 'was-disabled' : '');
      }
      btn.setAttribute('aria-disabled', 'true');
    });
    // Plakbalk: niet "Complete set / XS" tonen terwijl er nog geen maat gekozen is.
    document.querySelectorAll('.sticky-add-to-cart__variant').forEach((el) => {
      const current = el.textContent.trim();
      if (!current || current.endsWith(LABEL.toLowerCase())) return;
      /** @type {HTMLElement} */ (el).dataset.orionText = current;
      el.textContent = `${current.split(' / ')[0]} · ${LABEL.toLowerCase()}`;
    });
  };

  const release = () => {
    chosen = true;
    document.documentElement.classList.remove('orion-needs-size');
    document.querySelectorAll('.orion-size-pending').forEach((f) => f.classList.remove('orion-size-pending'));
    document.querySelectorAll('.orion-size-msg').forEach((m) => m.remove());
    document.querySelectorAll('button[name="add"][data-orion-locked]').forEach((btn) => {
      const text = [...btn.childNodes].find((n) => n.nodeType === 3 && n.textContent.trim());
      if (text && btn.dataset.orionText) text.textContent = btn.dataset.orionText;
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('data-orion-locked');
      delete btn.dataset.orionText;
    });
    document.querySelectorAll('.sticky-add-to-cart__variant[data-orion-text]').forEach((el) => {
      el.textContent = /** @type {HTMLElement} */ (el).dataset.orionText ?? '';
      el.removeAttribute('data-orion-text');
    });
    observer.disconnect();
  };

  // Klik op een geblokkeerde koopknop: niets toevoegen, wel uitleggen en naar de maten gaan.
  const showMessage = () => {
    const fieldset = document.querySelector('fieldset.orion-size-pending');
    if (!fieldset) return;
    let msg = fieldset.parentElement?.querySelector('.orion-size-msg');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'orion-size-msg';
      msg.setAttribute('role', 'status');
      fieldset.insertAdjacentElement('afterend', msg);
    }
    msg.textContent = 'Kies eerst je maat.';
    fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
    /** @type {HTMLElement|null} */ (fieldset.querySelector('input:not([aria-disabled="true"])'))?.focus({ preventScroll: true });
  };
  // Op window in de capture-fase: Horizon luistert zelf (capture) op document, en dat mag pas na deze controle.
  window.addEventListener(
    'click',
    (event) => {
      if (chosen) return;
      const btn = event.target instanceof Element ? event.target.closest('button[name="add"][data-orion-locked]') : null;
      if (!btn) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      showMessage();
    },
    true
  );
  window.addEventListener(
    'submit',
    (event) => {
      if (chosen) return;
      if (event.target instanceof HTMLFormElement && event.target.querySelector('button[name="add"][data-orion-locked]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showMessage();
      }
    },
    true
  );

  // Een tik op een maatknop (ook de al intern gekozen maat) telt als keuze.
  document.addEventListener('click', (event) => {
    const label = event.target instanceof Element ? event.target.closest('.variant-option__button-label') : null;
    const input = label?.querySelector(`input[data-option-name="${SIZE_OPTION}"]`);
    if (input && input.dataset.optionAvailable !== 'false') release();
  });
  // Horizon vervangt bij een andere uitvoering de knoppen: opnieuw blokkeren zolang er geen maat is gekozen.
  const observer = new MutationObserver(() => {
    if (!chosen) requestAnimationFrame(apply);
  });
  const main = document.querySelector('main');
  if (main) observer.observe(main, { childList: true, subtree: true });
  apply();
}

orionSizeGate();

/* ------------------------------------------------------------------ */
/* Kleurkeuze op productpagina: na Mint/Zwart/Zand naar de foto terug  */
/* ------------------------------------------------------------------ */

function orionColorMediaScroll() {
  const COLOR_OPTION = 'Kleur';
  let armed = false;
  let timer = 0;
  let pendingColor = '';

  const normalizeColor = (value) => {
    const v = String(value || '').trim().toLowerCase();
    if (v.includes('roze') || v.includes('pink')) return 'roze';
    if (v.includes('zwart') || v.includes('black')) return 'zwart';
    if (v.includes('wit') || v.includes('white')) return 'wit';
    if (v.includes('mint') || v.includes('groen') || v.includes('green')) return 'mint';
    if (v.includes('zand') || v.includes('sand') || v.includes('beige')) return 'zand';
    return v;
  };

  const productTopTarget = () =>
    document.querySelector('main media-gallery') ||
    document.querySelector('main .product-media-gallery') ||
    document.querySelector('main .product-media-container') ||
    document.querySelector('main [data-product-media-gallery]') ||
    document.querySelector('main');

  const findColorMedia = (color) => {
    color = normalizeColor(color);
    if (!color) return null;
    const gallery = document.querySelector('main media-gallery');
    if (!gallery) return null;
    const tokens = {
      roze: ['roze', 'pink'],
      zwart: ['zwart', 'black'],
      wit: ['wit', 'white'],
      mint: ['mint', 'groen', 'green'],
      zand: ['zand', 'sand', 'beige'],
    }[color] || [color];
    const slides = [...gallery.querySelectorAll('slideshow-slide, .product-media-container')];
    const matched = slides.find((slide) => {
      const img = slide.querySelector?.('img');
      const text = [img?.alt, img?.currentSrc, img?.src, slide.id, slide.textContent]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return tokens.some((token) => text.includes(token));
    });
    if (!matched) return null;
    const slide = matched.matches?.('slideshow-slide') ? matched : matched.closest?.('slideshow-slide') || matched;
    const index = slides.indexOf(matched);
    return { gallery, slide, index };
  };

  const activateColorMedia = (color) => {
    const found = findColorMedia(color);
    if (!found) return null;
    const { gallery, slide, index } = found;
    const slideId = slide?.id;
    const control = slideId
      ? gallery.querySelector(`button[aria-controls="${CSS.escape(slideId)}"], [aria-controls="${CSS.escape(slideId)}"]`)
      : null;
    if (control instanceof HTMLElement) control.click();
    else if (gallery.slideshow?.select && index >= 0) gallery.slideshow.select(index, undefined, { animate: true });
    return slide;
  };

  const scrollToProductPhoto = (color) => {
    const target = productTopTarget();
    if (!target) return;
    // Meerdere frames/timeouts omdat Horizon na variantwissel de media-gallery kan vervangen.
    const go = () => {
      const mediaTarget = activateColorMedia(color);
      const freshTarget = mediaTarget || productTopTarget() || target;
      const root = orionScrollRoot();
      const rect = freshTarget.getBoundingClientRect();
      const rootRect = root === document.scrollingElement ? { top: 0 } : root.getBoundingClientRect();
      const current = root === document.scrollingElement ? window.scrollY : root.scrollTop;
      const top = Math.max(0, rect.top - rootRect.top + current - 58);
      root.scrollTo({ top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      if (root !== document.scrollingElement) window.scrollTo({ top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    };
    requestAnimationFrame(() => requestAnimationFrame(go));
    window.setTimeout(go, 220);
    window.setTimeout(go, 650);
  };

  const arm = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const label = target?.closest('.variant-option__button-label, label');
    const input =
      label?.querySelector(`input[data-option-name="${COLOR_OPTION}"]`) ||
      target?.closest?.(`input[data-option-name="${COLOR_OPTION}"]`);
    if (!input) return;
    pendingColor = normalizeColor(input.value || input.getAttribute('value') || label?.textContent || '');
    armed = true;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      if (!armed) return;
      armed = false;
      scrollToProductPhoto(pendingColor);
    }, 900);
  };

  document.addEventListener('pointerup', arm, true);
  document.addEventListener('click', arm, true);
  document.addEventListener('change', arm, true);

  document.addEventListener(StandardEvents.productSelect, (event) => {
    if (!armed || !(event.target instanceof Element) || event.target.closest('product-card')) return;
    const done = () => {
      armed = false;
      window.clearTimeout(timer);
      scrollToProductPhoto(pendingColor);
    };
    event.promise?.then(done, done) ?? done();
  });
}

orionColorMediaScroll();

// "Kies de complete set": zolang er nog geen maat gekozen is, wisselen we alleen de uitvoering (geen vooraf gekozen maat).
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('a[data-orion-pick-set]') : null;
  if (!link) return;
  const input = [...document.querySelectorAll('main variant-picker input[data-option-name]')].find(
    (i) => /** @type {HTMLInputElement} */ (i).value === link.getAttribute('data-orion-pick-set')
  );
  if (!input) return;
  event.preventDefault();
  /** @type {HTMLElement} */ (input.closest('label') ?? input).click();
});


/* ------------------------------------------------------------------ */
/* Live blok: werkt zichzelf bij bij een variantwissel (Horizon stuurt  */
/* bij product:select de nieuwe sectie-HTML mee).                      */
/* ------------------------------------------------------------------ */

class OrionLive extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  #onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const fresh = detail?.html?.querySelector(`orion-live[data-key="${this.dataset.key}"]`);
        if (fresh) {
          this.innerHTML = fresh.innerHTML;
          this.dispatchEvent(new CustomEvent('orion-live:update', { bubbles: true }));
        }
      })
      .catch(() => {});
  };
}


/* Adviesknop niet over koop-/betaalknoppen heen */
function orionConciergeAvoid() {
  const concierge = document.querySelector('.orion-concierge');
  if (!concierge || !('IntersectionObserver' in window)) return;
  const inView = new Set();
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => (e.isIntersecting ? inView.add(e.target) : inView.delete(e.target)));
      concierge.classList.toggle('orion-concierge--away', inView.size > 0);
    },
    // Hele viewport: de knop staat onderin, dus juist daar mag niets overlappen.
    { rootMargin: '0px' }
  );
  const watch = () =>
    document
      .querySelectorAll(
        'main add-to-cart-component, main .shopify-payment-button, main accelerated-checkout, main orion-countdown, main variant-picker, main form button[type="submit"], .cart-page__summary, .sticky-add-to-cart__bar[data-stuck="true"], footer .payment-icons, footer .footer-utilities, footer [class*="policy"], .orion-footer__bottom, .orion-footer__club'
      )
      .forEach((el) => io.observe(el));
  watch();
  new MutationObserver(watch).observe(document.querySelector('main') ?? document.body, { childList: true, subtree: true });
}
orionConciergeAvoid();

/* Footer: groepen op desktop altijd open, op telefoon uitklapbaar (dicht bij laden) */
function orionFooterGroups() {
  const groups = [...document.querySelectorAll('.orion-footer__group')];
  if (!groups.length) return;
  const mobile = window.matchMedia('(max-width: 749px)');
  const sync = () => groups.forEach((g) => (g.open = !mobile.matches));
  sync();
  mobile.addEventListener('change', sync);
  groups.forEach((g) =>
    g.querySelector('summary')?.addEventListener('click', (e) => {
      if (!mobile.matches) e.preventDefault();
    })
  );
}
orionFooterGroups();

/* Sitebrede snelle navigatie: omhoog + omlaag */
function orionScrollNav() {
  if (document.querySelector('.orion-scrollnav')) return;
  const nav = document.createElement('div');
  nav.className = 'orion-scrollnav';
  nav.innerHTML = `
    <button type="button" class="orion-scrollnav__btn" data-orion-scroll="up" aria-label="Naar boven">↑</button>
    <button type="button" class="orion-scrollnav__btn" data-orion-scroll="down" aria-label="Verder omlaag">↓</button>
  `;
  document.body.append(nav);

  const maxScroll = () => {
    const root = orionScrollRoot();
    return root.scrollHeight - root.clientHeight;
  };

  const currentScroll = () => {
    const root = orionScrollRoot();
    return root === document.scrollingElement ? window.scrollY : root.scrollTop;
  };

  const update = () => {
    const y = currentScroll();
    nav.toggleAttribute('data-at-top', y < 120);
    nav.toggleAttribute('data-at-bottom', y > maxScroll() - 160);
  };

  nav.addEventListener('click', (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-orion-scroll]') : null;
    if (!btn) return;
    const root = orionScrollRoot();
    if (btn.getAttribute('data-orion-scroll') === 'up') {
      root.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      return;
    }

    const y = currentScroll();
    const sections = [...document.querySelectorAll('main section, main .shopify-section, footer')].filter(
      (el) => el.getBoundingClientRect().height > 80
    );
    const next = sections.find((el) => {
      const top = el.getBoundingClientRect().top + y;
      return top > y + window.innerHeight * 0.35;
    });
    if (next) orionScrollToElement(next, { offset: 72 });
    else root.scrollTo({ top: maxScroll(), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  });

  window.addEventListener('scroll', update, { passive: true });
  orionScrollRoot().addEventListener?.('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();
}

orionScrollNav();

/* Band pauzeren / hervatten */
document.addEventListener('click', (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-orion-marquee-toggle]') : null;
  if (!btn) return;
  const band = btn.closest('.orion-marquee');
  const paused = band?.classList.toggle('is-paused') ?? false;
  btn.setAttribute('aria-pressed', String(paused));
  btn.setAttribute('aria-label', paused ? 'Bewegende tekst hervatten' : 'Bewegende tekst pauzeren');
});

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

define('orion-delivery-estimate', OrionDeliveryEstimate);
define('orion-countdown', OrionCountdown);
define('orion-bundle-offer', OrionBundleOffer);
define('orion-upsell-add', OrionUpsellAdd);
define('orion-dialog-trigger', OrionDialogTrigger);
define('orion-popup', OrionPopup);
define('orion-video-rail', OrionVideoRail);
define('orion-contact-form', OrionContactForm);
define('orion-live', OrionLive);

/* ---------- Gratis verzending op de productpagina: commercieel + live ---------- */
{
  const formatMoney = (cents, currency) =>
    new Intl.NumberFormat('nl-NL', { style: 'currency', currency: currency || 'EUR' }).format(cents / 100).replace(/\s/g, '');

  const escapeHtml = (text) => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

  const todayCountdown = () => {
    // ORION verkoopt NL-first: dagactie reset op 00:00 Amsterdam-tijd, niet op server/browser-UTC.
    const now = new Date();
    let amsterdamNow;
    try {
      amsterdamNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }));
    } catch {
      amsterdamNow = now;
    }
    const midnight = new Date(amsterdamNow);
    midnight.setDate(midnight.getDate() + 1);
    midnight.setHours(0, 0, 0, 0);
    const total = Math.max(0, Math.floor((midnight.getTime() - amsterdamNow.getTime()) / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const sec = total % 60;
    return [h, m, sec].map((n) => String(n).padStart(2, '0')).join(':');
  };

  const renderShipCountdowns = () => {
    document.querySelectorAll('[data-orion-ship-countdown]').forEach((el) => {
      el.textContent = todayCountdown();
    });
  };

  const renderShipBars = (cart = {}) => {
    for (const bar of document.querySelectorAll('[data-orion-ship-live]')) {
      const threshold = Number(bar.dataset.threshold) || 0;
      if (!threshold) continue;

      // Productpagina: reken commercieel met de actuele productprijs, niet met een lege winkelwagen.
      // Winkelwagen/drawer-balken zonder data-current-price blijven gewoon op cart.total_price werken.
      const currentPrice = Number(bar.dataset.currentPrice) || 0;
      const total = currentPrice || cart.total_price || 0;
      const remaining = threshold - total;
      const progress = Math.min(100, Math.max(0, Math.floor((total * 100) / threshold)));

      bar.hidden = false;
      bar.toggleAttribute('data-done', remaining <= 0);
      bar.style.setProperty('--orion-progress', `${progress}%`);

      const label = bar.querySelector('[data-orion-ship-label]');
      if (label) {
        label.innerHTML =
          remaining > 0
            ? escapeHtml(bar.dataset.textRemaining || 'Nog [bedrag] en je verzending is gratis').replace(
                '[bedrag]',
                `<strong>${formatMoney(remaining, cart.currency || 'EUR')}</strong>`
              )
            : escapeHtml(bar.dataset.textDone || 'Gratis verzending geactiveerd');
      }

      const percent = bar.querySelector('[data-orion-ship-percent]');
      if (percent) percent.textContent = `${progress}%`;
    }
    renderShipCountdowns();
  };

  const refreshShipBars = async () => {
    if (!document.querySelector('[data-orion-ship-live]')) return;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (response.ok) renderShipBars(await response.json());
      else renderShipBars({ currency: 'EUR' });
    } catch {
      renderShipBars({ currency: 'EUR' });
    }
  };

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.promise) event.promise.then(refreshShipBars, () => {});
    else refreshShipBars();
  });

  document.addEventListener(StandardEvents.productSelect, (event) => {
    event.promise?.then(() => window.setTimeout(refreshShipBars, 80), () => window.setTimeout(refreshShipBars, 80));
  });

  window.addEventListener('pageshow', refreshShipBars);
  renderShipBars({ currency: 'EUR' });
  window.setInterval(renderShipCountdowns, 1000);
}

/* ---------- "Je kiest": prijs met actiecode zodra die echt actief is ---------- */
{
  let cartCodes = [];

  const cookieCode = () => {
    const raw = document.cookie.split('; ').find((c) => c.startsWith('discount_code='));
    return raw ? decodeURIComponent(raw.split('=')[1] || '').toUpperCase() : '';
  };

  const activePromo = () => {
    const countdown = document.querySelector('orion-countdown[data-code][data-pct]:not([hidden]):not([data-ended])');
    if (!countdown) return null;
    const code = countdown.dataset.code || '';
    const pct = Number(countdown.dataset.pct) || 0;
    if (!code || pct <= 0) return null;
    return cookieCode() === code || cartCodes.includes(code) ? { code, pct } : null;
  };

  const money = (cents) =>
    new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(cents / 100).replace(/\s/g, '');

  const renderDeals = () => {
    const promo = activePromo();
    for (const deal of document.querySelectorAll('[data-orion-deal-price]')) {
      const regular = deal.parentElement?.querySelector('[data-orion-regular-price]');
      const price = Number(deal.dataset.orionDealPrice) || 0;
      if (!promo || !price) {
        deal.hidden = true;
        if (regular) regular.hidden = false;
        continue;
      }
      const discounted = Math.round((price * (100 - promo.pct)) / 100);
      deal.innerHTML = `<s>${money(price)}</s><strong>${money(discounted)}</strong><span class="orion-selection__code">${promo.code} actief</span>`;
      deal.hidden = false;
      if (regular) regular.hidden = true;
    }
  };

  const refreshCartCodes = async () => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      cartCodes = (cart.cart_level_discount_applications || [])
        .concat(...(cart.items || []).map((item) => item.line_level_discount_allocations?.map((a) => a.discount_application) || []))
        .filter((a) => a && a.type === 'discount_code')
        .map((a) => String(a.title || '').toUpperCase());
    } catch {
      /* alleen de cookie telt dan */
    }
    renderDeals();
  };

  document.addEventListener('orion-live:update', renderDeals);
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.promise) event.promise.then(refreshCartCodes, () => {});
    else refreshCartCodes();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', renderDeals, { once: true });
  else renderDeals();
  if (document.querySelector('[data-orion-deal-price]')) refreshCartCodes();
}

/* ORION: hard language switcher. Shopify localization did not visibly switch routes on ORION markets. */
(function () {
  function setManualLocaleCookie() {
    try { document.cookie = 'orion_locale_redirect_done=1; path=/; max-age=2592000; SameSite=Lax'; } catch (e) {}
  }
  function targetForLanguage(lang) {
    // Altijd één leidende slash: '//host' of '/\host' zou anders naar een ander domein springen (open redirect).
    var path = '/' + String(window.location.pathname || '/').replace(/^[\/\\]+/, '');
    var search = window.location.search || '';
    var hash = window.location.hash || '';
    lang = String(lang || '').toLowerCase();
    if (lang.indexOf('en') === 0) {
      if (/^\/en(?:\/|$)/.test(path)) return path + search + hash;
      return '/en' + (path === '/' ? '/' : path) + search + hash;
    }
    if (lang.indexOf('nl') === 0) {
      var clean = '/' + path.replace(/^\/en(?=\/|$)/, '').replace(/^[\/\\]+/, '');
      return clean + search + hash;
    }
    return null;
  }
  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    if (select.name !== 'language_code') return;
    var target = targetForLanguage(select.value);
    if (!target) return;
    setManualLocaleCookie();
    if (target !== window.location.pathname + window.location.search + window.location.hash) {
      event.preventDefault();
      window.location.assign(target);
    }
  }, true);
})();
